import { MaterialCommunityIcons } from "@expo/vector-icons";
import { isSupervisoryVisitTypeName, type DiagnosisMap } from "@taskhealth/shared";
import { useFocusEffect, useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AppState, Platform, Pressable, ScrollView, StyleSheet, View } from "react-native";
import { Snackbar, Text } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

import {
    AssessmentScreenHeader,
    assessmentScreenStyles
} from "@/components/assessment/AssessmentScreenHeader";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { DiagnosisHubFAB, DiagnosisHubSheet } from "@/components/diagnosis-hub";
import { isBodyMetricsComplete } from "@/components/questions/bmi-classify";
import {
    CompleteChapter,
    type CompletePhase,
    useValidatingStageIdx
} from "@/components/section/CompleteChapter";
import { ContextAnswerReview } from "@/components/section/ContextAnswerReview";
import {
    DiagnosesChapter,
    buildDiagnosisRows,
    type DiagnosesPhase
} from "@/components/section/DiagnosesChapter";
import {
    NarrativeChapter,
    type NarrativeChapterHandle
} from "@/components/section/NarrativeChapter";
import {
    QuestionsChapter,
    ValidationErrorCardWrapper
} from "@/components/section/QuestionsChapter";
import { PerformanceEvaluationStep } from "@/components/steps/PerformanceEvaluationStep";
import { PocReviewStep } from "@/components/steps/poc-review";
import { SupervisoryStep } from "@/components/steps/SupervisoryStep";
import { ContextItemSheet } from "@/components/visit/ContextItemSheet";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";
import { useContextAnswerReview } from "@/hooks/useContextAnswerReview";
import { useDiagnosisHub } from "@/hooks/useDiagnosisHub";
import { useHintScrollAnchor } from "@/hooks/useHintScrollAnchor";
import { useValidationFlow } from "@/hooks/useValidationFlow";
import { useVisitContext } from "@/hooks/useVisitContext";
import {
    ApiError,
    completeSection,
    deleteVisitDiagnosisById,
    fetchIcdSuggestions,
    fetchPatientDiagnoses,
    fetchSectionState,
    saveDiagnoses,
    saveNarrative,
    type ContextHint,
    type ContextItemView,
    type ICDSuggestion,
    type PatientDiagnosisItem,
    type ValidationError
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { answersOrphanedBy, evaluateVisibility, type QuestionWithRules } from "@/lib/branching";
import { buildContextAnswerReview } from "@/lib/context-hint-review";
import { hasDiagnosisAddedToThisSection } from "@/lib/diagnosis-completion-guard";
import {
    deriveAnswerBasedSuggestions,
    loadDiagnosisMap,
    type AnswerBasedSuggestion
} from "@/lib/diagnosis-map";
import { DraftRegistryContext, type DraftHolder, type DraftRegistry } from "@/lib/DraftRegistry";
import { useFormContext } from "@/lib/FormContext";
import { colors, fonts } from "@/lib/theme";

// Question types that can't be answered on mobile — skip required validation
const PLACEHOLDER_TYPES = new Set(["locked", "icdCodes", "medicationProfile"]);

/** What `flushPendingWrites` found still unsent, so the caller can name it. */
type FlushOutcome = { ok: true } | { ok: false; what: "answers" | "narrative" };

function flushErrorMessage(what: "answers" | "narrative"): string {
    return what === "narrative"
        ? "Couldn't save your narrative — check your connection and try again."
        : "Couldn't save your answers — check your connection and try again.";
}

/** Chapter set per behavior type. Drives which chapter wrappers render. */
function chaptersFor(behaviorType: string): {
    questions: boolean;
    narrative: boolean;
    diagnoses: boolean;
    complete: boolean;
} {
    switch (behaviorType) {
        case "clinical_5step":
            return { questions: true, narrative: true, diagnoses: true, complete: true };
        case "clinical_4step":
            return { questions: true, narrative: true, diagnoses: false, complete: true };
        case "data_entry_validated":
            return { questions: true, narrative: false, diagnoses: false, complete: true };
        case "medical_history":
            return { questions: true, narrative: false, diagnoses: true, complete: false };
        case "data_entry":
        case "medications":
        default:
            return { questions: true, narrative: false, diagnoses: false, complete: false };
    }
}

/**
 * Flat section screen — replaces the wizard at section/[sectionId].tsx.
 *
 * The wizard rendered one step at a time (questions → narrative → icd →
 * validation). This file stacks every chapter on one scrollable surface
 * and replaces the implicit "Next-tap-triggers-the-AI" with explicit buttons:
 *
 *   - "Run AI scan" inside the Diagnoses chapter (replaces the wizard's auto-
 *     fire of fetchIcdSuggestions on entering the ICD step)
 *   - "Complete section" inside the Complete chapter (replaces the auto-fire
 *     of triggerValidation on entering the Validation step)
 *
 * Hooks + APIs are unchanged from the wizard:
 *   - useFormContext (autosave, answers, saveStatus)
 *   - useValidationFlow (same /ai/validate plumbing, error state, dismiss)
 *   - fetchIcdSuggestions / fetchPatientDiagnoses / saveDiagnoses
 *   - saveNarrative / fetchSectionState
 *   - completeSection (final commit)
 *
 * Special behavior types fall through to existing dedicated step components:
 *   - plan_of_care → PocReviewStep
 *   - supervisory_evaluation → SupervisoryStep
 *   - performance_evaluation → PerformanceEvaluationStep
 */
export default function SectionScreen() {
    const { id: visitId, sectionId } = useLocalSearchParams<{ id: string; sectionId: string }>();
    const router = useRouter();
    const { form, answers, setAnswer, flushSaves, saveStatus, getSection, readOnly, updateForm } =
        useFormContext();

    const section = useMemo(
        () => (sectionId ? getSection(sectionId) : undefined),
        [getSection, sectionId]
    );
    const behaviorType = section?.behaviorType ?? "data_entry";
    const chapters = useMemo(() => chaptersFor(behaviorType), [behaviorType]);
    const questions = section?.questions ?? [];

    // Freeze context on section entry. Additional context remains inline;
    // answer differences are checked locally only on completion.
    const { context: patientContext } = useVisitContext(visitId, !readOnly, { poll: false });
    const [contextSheet, setContextSheet] = useState<{
        title: string;
        items: ContextItemView[];
        questionId: number;
    } | null>(null);
    const contextHints = useMemo(() => {
        const map = new Map<string, ContextHint[]>();
        for (const hint of patientContext?.hints ?? []) {
            const key = String(hint.questionId);
            map.set(key, [...(map.get(key) ?? []), hint]);
        }
        return map;
    }, [patientContext]);
    const openContextHint = useCallback(
        (hint: ContextHint) => {
            if (hint.sourceViewEnabled === false) {
                return;
            }
            const items = (patientContext?.sections ?? [])
                .flatMap((sec) => sec.items)
                .filter((i) => hint.itemIds.includes(i.id) && i.canOpenSource);
            if (items.length > 0) {
                setContextSheet({ title: hint.sourceLabel, items, questionId: hint.questionId });
            }
        },
        [patientContext]
    );

    // ── Local state ────────────────────────────────────────────────────────
    const [narrativeText, setNarrativeText] = useState("");
    const narrativeRef = useRef<NarrativeChapterHandle | null>(null);

    const [requiredErrors, setRequiredErrors] = useState<Set<string>>(new Set());
    const [completing, setCompleting] = useState(false);
    const [validated, setValidated] = useState(false);

    // ── Validation phase state (spec: validation-errors-flow/SPEC.md) ─────
    const [completePhase, setCompletePhase] = useState<CompletePhase>("idle");
    const [dismissedLocalIds, setDismissedLocalIds] = useState<Set<number>>(new Set());
    // The narrative lives in ONE place — the Narrative chapter. There is no
    // longer a per-error inline composer or "narrative-saved" status on the
    // validation cards. Card has two states: active / dismissed.

    // Stage indicator driven by the "validating" phases. Resets to 0 on
    // phase change; advances every 2.4s up to 4.
    const validatingActive = completePhase === "validating" || completePhase === "revalidating";
    const validatingStageIdx = useValidatingStageIdx(validatingActive);

    const [localError, setLocalError] = useState<{ msg: string | null; tick: number }>({
        msg: null,
        tick: 0
    });
    const setError = useCallback((msg: string | null) => {
        setLocalError((prev) => ({ msg, tick: prev.tick + 1 }));
    }, []);

    const scrollRef = useRef<ScrollView>(null);
    const hintQuestionRefs = useRef(new Map<string, View>());
    const { onScroll: onHintScroll, scrollToView } = useHintScrollAnchor(
        scrollRef,
        hintQuestionRefs
    );
    // Used to confirm before a branch change deletes answers underneath it.
    // Alert.alert is a no-op on Expo web, so this must be the dialog component.
    const { dialogState, showDialog, dismissDialog } = useConfirmDialog();
    const questionYPositions = useRef<Map<string, number>>(new Map());
    const narrativeTargetRef = useRef<View>(null);
    const [narrativeHighlightTick, setNarrativeHighlightTick] = useState(0);
    const diagnosesYRef = useRef<number | null>(null);
    const addManuallyYRef = useRef<number | null>(null);
    const completeYRef = useRef<number | null>(null);
    // In-flight scan controller — answer/narrative edits abort it so a
    // stale scan result never overwrites the user's latest edit.
    const scanAbortRef = useRef<AbortController | null>(null);
    // The 1.2 s "clear → complete" timer. Held so leaving the screen cancels it:
    // a timer that outlives the screen would complete the section unseen.
    const completionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Sticky flag: true once the RN has tapped Run AI scan at least once in
    // this section entry. Complete is blocked until either this is true OR
    // the section already has prior AI-originated rows (selected from a past
    // session) — see handleValidatePress.
    const hasScannedRef = useRef(false);
    // Narrative dirty-tracking — true means local text is ahead of server.
    // Used by the focus-effect to skip overwriting in-progress typing, and
    // by the unmount cleanup to fire a final flush.
    const narrativeDirtyRef = useRef(false);
    const narrativeLatestRef = useRef("");

    // ── Diagnoses state machine ───────────────────────────────────────────
    const [diagnosesPhase, setDiagnosesPhase] = useState<DiagnosesPhase>("idle");
    // Server-authoritative diagnosis state. The displayed rows are DERIVED
    // (see `rows` below) from the latest fresh server read + the latest scan
    // suggestions — never a separate optimistic list that can drift.
    const [suggestions, setSuggestions] = useState<ICDSuggestion[]>([]);
    // Codes whose write (select / add / delete) is in flight — drives the
    // per-row pending UI in DiagnosesChapter.
    const [pendingCodes, setPendingCodes] = useState<Set<string>>(new Set());
    const [addManuallyHighlightTick, setAddManuallyHighlightTick] = useState(0);
    // Symptom→diagnosis matrix, cached once per session (lib/diagnosis-map).
    // Drives the deterministic "Based on your answers" group — no LLM.
    const [diagnosisMap, setDiagnosisMap] = useState<DiagnosisMap>({});
    useEffect(() => {
        let alive = true;
        void loadDiagnosisMap()
            .then((m) => {
                if (alive) {
                    setDiagnosisMap(m);
                }
            })
            .catch(() => {
                // Offline / error — the group simply stays empty; the LLM scan
                // remains available as the fallback path.
            });
        return () => {
            alive = false;
        };
    }, []);
    // DiagnosisSearchSheet was removed in the flat redesign — Add More opens
    // an inline panel inside DiagnosesChapter instead of a modal sheet.

    // Required-field validation
    const childQuestionsByParent = useMemo(() => {
        const map = new Map<string, typeof questions>();
        for (const q of questions) {
            if (q.parentQuestionId) {
                const list = map.get(q.parentQuestionId) ?? [];
                list.push(q);
                map.set(q.parentQuestionId, list);
            }
        }
        return map;
    }, [questions]);

    // Branching-aware visible set — same path the renderer uses. The wizard
    // (main) derives both validateRequired and visibleQuestionIdsList from
    // this; the flat redesign must too, otherwise hidden branching targets
    // get flagged as required-missing.
    const mobileQuestions = useMemo(
        () => questions.filter((q) => q.visibleOnMobile !== false && q.isRetired !== true),
        [questions]
    );
    const questionsWithRules: QuestionWithRules[] = useMemo(
        () =>
            mobileQuestions.map((q) => ({
                id: q.id,
                branchingRules: q.branchingRules ?? []
            })),
        [mobileQuestions]
    );
    const visibleIdSet = useMemo(
        () => evaluateVisibility(questionsWithRules, answers),
        [questionsWithRules, answers]
    );
    const visibleQuestions = useMemo(
        () => mobileQuestions.filter((q) => visibleIdSet.has(q.id)),
        [mobileQuestions, visibleIdSet]
    );
    const visibleQuestionIdsList = useMemo(
        () => visibleQuestions.map((q) => String(q.id)),
        [visibleQuestions]
    );

    const { user } = useAuth();
    const answerReviewItems = useMemo(
        () => buildContextAnswerReview(visibleQuestions, answers, patientContext?.hints ?? []),
        [visibleQuestions, answers, patientContext]
    );
    const answerReview = useContextAnswerReview(
        `${user?.id ?? "unknown"}.${visitId}.${sectionId}`,
        answerReviewItems
    );
    const reviewTargetRef = useRef<View>(null);
    const completionBusy = useRef(false);
    useEffect(() => {
        if (answerReview.pending !== null) {
            const frame = requestAnimationFrame(() => {
                if (reviewTargetRef.current) {
                    scrollToView(reviewTargetRef.current, 24);
                }
            });
            return () => cancelAnimationFrame(frame);
        }
    }, [answerReview.pending !== null, scrollToView]);

    // ── Validation flow hook (errors + dismiss) ──
    const lastValidationComputedAtRef = useRef<string | null>(null);
    const validation = useValidationFlow({
        visitId: visitId ?? "",
        sectionId: sectionId ?? "",
        flushSaves,
        visibleQuestionIds: visibleQuestionIdsList,
        showSnackbar: setError,
        lastValidationComputedAtRef
    });

    // ── Focus refresh — server is source of truth for narrative + errors ──
    const syncErrorsFromServer = validation.syncErrorsFromServer;
    useFocusEffect(
        useCallback(() => {
            if (!visitId || !sectionId || readOnly) {
                return;
            }
            let cancelled = false;
            void (async () => {
                try {
                    const state = await fetchSectionState(visitId, sectionId);
                    if (cancelled) {
                        return;
                    }
                    // Don't clobber in-progress typing — if the local
                    // narrative is ahead of what we just refetched, the
                    // RN is mid-edit and a focus refresh would lose her
                    // unsaved keystrokes. Old wizard's narrativeOpenForRef
                    // guard, restored.
                    if (!narrativeDirtyRef.current) {
                        setNarrativeText(state.narrativeText);
                    }
                    syncErrorsFromServer(state.validationErrors);
                    lastValidationComputedAtRef.current = state.validationComputedAt;
                } catch (e) {
                    // An aborted GET means the screen blurred/unmounted before
                    // the refresh finished — expected, not an error worth logging.
                    if (e instanceof ApiError && e.kind === "abort") {
                        return;
                    }
                    console.error("Failed to refresh section state:", e);
                }
            })();
            return () => {
                cancelled = true;
            };
        }, [visitId, sectionId, readOnly, syncErrorsFromServer])
    );

    // ── Server-authoritative diagnosis list ──────────────────────────────
    // `serverDiagnoses` is the latest FRESH read of the merged diagnosis list
    // (confirmed visit_diagnoses + patient_diagnoses profile). It is NOT a copy
    // cached once on entry — it is re-read on focus, on app foreground, after
    // every confirmed write, and at scan start. The displayed `rows` derive
    // from it (+ the scan `suggestions`), so a deleted code can never be
    // resurrected from a stale local copy.
    const [serverDiagnoses, setServerDiagnoses] = useState<PatientDiagnosisItem[]>([]);
    // In-flight write promises. Reads (refresh / scan) await these before
    // fetching so they never observe pre-write state. A SET, not a single ref,
    // because pessimistic per-row writes can overlap.
    const pendingWritesRef = useRef<Set<Promise<unknown>>>(new Set());
    // Monotonic epoch so out-of-order refresh GETs can't commit stale data —
    // only the newest-issued read writes to `serverDiagnoses`.
    const diagFetchEpochRef = useRef(0);
    const isFocusedRef = useRef(false);

    const refreshServerDiagnoses = useCallback(async () => {
        if (!visitId) {
            return;
        }
        // Serialize against any in-flight write so we read post-write truth.
        if (pendingWritesRef.current.size > 0) {
            await Promise.allSettled([...pendingWritesRef.current]);
        }
        const epoch = ++diagFetchEpochRef.current;
        try {
            const fresh = await fetchPatientDiagnoses(visitId);
            // Only the newest-issued read commits — discards an earlier, slower GET.
            if (epoch === diagFetchEpochRef.current) {
                setServerDiagnoses(fresh);
            }
        } catch {
            // Non-fatal; leave the last good list in place.
        }
    }, [visitId]);

    // Fresh read on focus (mount / re-enter / app-reopen); also tracks focus
    // for the AppState foreground refresh below.
    useFocusEffect(
        useCallback(() => {
            isFocusedRef.current = true;
            void refreshServerDiagnoses();
            return () => {
                isFocusedRef.current = false;
            };
        }, [refreshServerDiagnoses])
    );

    // Background→foreground refresh — useFocusEffect doesn't fire on foreground
    // (the screen never lost focus), so cover it here while focused.
    useEffect(() => {
        const sub = AppState.addEventListener("change", (s) => {
            if (s === "active" && isFocusedRef.current) {
                void refreshServerDiagnoses();
            }
        });
        return () => sub.remove();
    }, [refreshServerDiagnoses]);

    // Single shared write wrapper — EVERY diagnosis write (select / add /
    // delete) goes through this. It registers its promise in `pendingWritesRef`
    // (so reads serialize behind it), flags the per-row pending state, and
    // re-reads the server afterward. On failure the row stays in its prior
    // server-confirmed state with a surfaced error — the UI never diverges
    // from the server. Pessimistic: the UI reflects only confirmed state.
    const runWrite = useCallback(
        async (code: string, op: () => Promise<void>, errMsg: string): Promise<void> => {
            if (!visitId || !sectionId) {
                return;
            }
            setPendingCodes((s) => {
                const n = new Set(s);
                n.add(code);
                return n;
            });
            const p = op();
            pendingWritesRef.current.add(p);
            try {
                await p;
            } catch {
                setError(errMsg);
            } finally {
                pendingWritesRef.current.delete(p);
                setPendingCodes((s) => {
                    const n = new Set(s);
                    n.delete(code);
                    return n;
                });
                await refreshServerDiagnoses();
            }
        },
        [visitId, sectionId, refreshServerDiagnoses, setError]
    );

    // Displayed rows — DERIVED, the single on-screen source of truth. No
    // separate optimistic `diagnoses` state to drift out of sync.
    const rows = useMemo(
        () => buildDiagnosisRows(suggestions, serverDiagnoses, sectionId),
        [suggestions, serverDiagnoses, sectionId]
    );

    // Option ids the RN has currently selected across this section's questions
    // (single value OR JSON-array multiselect), resolved against the form's
    // answer options. Pure function of the answers → recomputes live as the RN
    // ticks/unticks symptoms (unlike the LLM scan).
    //
    // Scoped to VISIBLE questions only (branching-active) so a follow-up answer
    // left behind on an inactive branch — e.g. an "Epistaxis frequency" child
    // whose parent symptom isn't selected — never surfaces a suggestion.
    const selectedOptionIds = useMemo(() => {
        const ids: number[] = [];
        for (const q of visibleQuestions) {
            const raw = answers[q.id];
            if (raw == null || raw === "") {
                continue;
            }
            let values: string[];
            try {
                const parsed = JSON.parse(raw);
                values = Array.isArray(parsed) ? parsed.map(String) : [String(raw)];
            } catch {
                values = [String(raw)];
            }
            const opts = q.answerOptions ?? [];
            for (const v of values) {
                const match = opts.find((o) => o.value === v);
                if (match) {
                    // AnswerOption.id is a string on mobile; the diagnosis map is
                    // keyed by the numeric option id.
                    ids.push(Number(match.id));
                }
            }
        }
        return ids;
    }, [visibleQuestions, answers]);

    // Deterministic answer-based suggestions for this section. Excludes any code
    // the RN already has (profile or confirmed this visit) so a selected code
    // drops out of the group and surfaces in the Selected zone instead.
    const answerBased = useMemo<AnswerBasedSuggestion[]>(() => {
        if (selectedOptionIds.length === 0) {
            return [];
        }
        const exclude = new Set(serverDiagnoses.map((d) => d.icdCode));
        return deriveAnswerBasedSuggestions(selectedOptionIds, diagnosisMap, exclude);
    }, [selectedOptionIds, diagnosisMap, serverDiagnoses]);

    // ── Diagnosis hub FAB ──
    const hub = useDiagnosisHub(visitId ?? "");
    const hubOpen = validation.activeSheet === "hub";

    // ── Auto-dismiss errors whose question is no longer visible (branching) ──
    const visibleIdsKey = useMemo(() => visibleQuestionIdsList.join(","), [visibleQuestionIdsList]);
    useEffect(() => {
        const visibleSet = new Set(visibleIdsKey.split(",").filter(Boolean));
        for (const err of validation.activeErrors) {
            if (err.id != null && err.questionId != null && !visibleSet.has(err.questionId)) {
                validation.dismissError(err.id);
            }
        }
    }, [visibleIdsKey, validation]);

    // ── Abort any in-flight validation on unmount ──
    const cancelRef = useRef(validation.cancel);
    cancelRef.current = validation.cancel;
    useEffect(() => {
        return () => {
            cancelRef.current();
            if (completionTimerRef.current) {
                clearTimeout(completionTimerRef.current);
                completionTimerRef.current = null;
            }
        };
    }, []);

    // ── Auto-delete unselected AI suggestions ──
    // Canonical chapter-03 rule: every Ch01 answer change, every narrative
    // edit, and every Complete-tap wipes the pending AI suggestions that the
    // RN hasn't selected. Absence of Select = rejection. The next time the
    // RN wants suggestions she taps Suggest Diagnosis again — phase flips
    // back to "idle" so the CTA is what she sees.
    // Defense in depth: in read-only states (sealed visit, section under
    // review, etc.) we never mutate diagnoses.
    const dropUnselectedAiSuggestions = useCallback(() => {
        if (readOnly) {
            return;
        }
        // Abort any in-flight scan — its result would be stale against the
        // edit that triggered this call.
        if (scanAbortRef.current) {
            scanAbortRef.current.abort();
            scanAbortRef.current = null;
        }
        // Server-authoritative model: pending suggestions live only in the
        // `suggestions` state — clearing it drops every unselected suggestion.
        // Selected codes are already persisted server-side and remain in `rows`.
        setSuggestions([]);
        setDiagnosesPhase((p) => (p === "results" || p === "analyzing" ? "idle" : p));
    }, [readOnly]);

    // ── handleChange — clears the required-error flag on the touched field ──
    /** Apply an answer edit. Assumes any orphan confirmation has already happened. */
    const applyChange = useCallback(
        (qid: string, value: string, alsoClear: string[] = []) => {
            setAnswer(qid, value);
            // Closing a branch must take its answers with it. Without this the
            // follow-ups vanish from the screen but stay in visit_answers, where
            // the PDF, the branched-summary LLM and the correction bot still read
            // them — a production scan found this on ~21% of visits.
            for (const orphan of alsoClear) {
                setAnswer(orphan, "");
            }
            if (requiredErrors.has(qid)) {
                setRequiredErrors((prev) => {
                    const next = new Set(prev);
                    next.delete(qid);
                    return next;
                });
            }
            // Any answer edit invalidates the scan — drop pending suggestions.
            dropUnselectedAiSuggestions();
        },
        [setAnswer, requiredErrors, dropUnselectedAiSuggestions]
    );

    const handleChange = useCallback(
        (questionId: string, value: string) => {
            const qid = String(questionId);

            // Would this change hide questions the nurse has already answered?
            // Ask before destroying them — she may simply have mis-tapped, and
            // there is no undo. Silence is not an option either: keeping them is
            // what creates the ghost answers in the first place.
            const orphaned = answersOrphanedBy(
                questions.map((q) => ({ id: q.id, branchingRules: q.branchingRules ?? [] })),
                answers,
                qid,
                value
            );

            if (orphaned.length === 0) {
                applyChange(qid, value);
                return;
            }

            const labels = orphaned
                .map((id) => questions.find((q) => q.id === id)?.text ?? `Question ${id}`)
                .slice(0, 5);
            const more = orphaned.length - labels.length;
            showDialog(
                "This will delete answers below",
                `Changing this answer hides ${orphaned.length} question${
                    orphaned.length === 1 ? "" : "s"
                } you have already answered. ${
                    orphaned.length === 1 ? "Its answer" : "Their answers"
                } will be deleted:\n\n` +
                    labels.map((t) => `• ${t}`).join("\n") +
                    (more > 0 ? `\n• …and ${more} more` : "") +
                    "\n\nAre you sure?",
                [
                    {
                        text: "Yes, delete",
                        style: "destructive",
                        onPress: () => applyChange(qid, value, orphaned)
                    },
                    { text: "No, keep them", style: "cancel" }
                ]
            );
        },
        [questions, answers, applyChange, showDialog]
    );

    // Narrative — debounced save mirrors the live app's PUT pattern.
    // narrativeDirtyRef/narrativeLatestRef are declared up with the other
    // refs; we just need the debounce timer here.
    const narrativeSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    narrativeLatestRef.current = narrativeText;

    /**
     * The one place a narrative write is issued. Serialized: one request in the
     * air, later callers wait and then send whatever the text is by then.
     *
     * The narrative is a full-text replace, so two overlapping saves can land
     * out of order and let the OLDER body win — silently, since neither call
     * errors. While the 1500ms debounce was the only writer that needed a slow
     * link and a well-timed keystroke; now that the diagnosis search and
     * Complete both flush it on demand, overlap is ordinary. Same shape as
     * AutoSaveEngine.flush(), for the same reason.
     *
     * Returns true when the server has the current text — including the case
     * where there was nothing to send.
     */
    const narrativeInFlightRef = useRef<Promise<boolean> | null>(null);
    const saveNarrativeNow = useCallback(async (): Promise<boolean> => {
        if (!visitId || !sectionId) {
            return true;
        }
        // An explicit save supersedes the pending debounce.
        if (narrativeSaveTimerRef.current) {
            clearTimeout(narrativeSaveTimerRef.current);
            narrativeSaveTimerRef.current = null;
        }
        // Read the chapter's own state — it can be a render ahead of
        // `narrativeText`, and the RN's last word is exactly what we're here for.
        narrativeLatestRef.current = narrativeRef.current?.getText() ?? narrativeLatestRef.current;
        if (!narrativeDirtyRef.current) {
            return true;
        }
        const run = async (): Promise<boolean> => {
            if (!narrativeDirtyRef.current) {
                return true;
            }
            const text = narrativeLatestRef.current;
            try {
                await saveNarrative(visitId, sectionId, text);
                // Only clear dirty if nothing was typed while we were away —
                // otherwise the newer text would never be sent.
                if (narrativeLatestRef.current === text) {
                    narrativeDirtyRef.current = false;
                }
                return true;
            } catch {
                return false;
            }
        };
        const pass = (narrativeInFlightRef.current ?? Promise.resolve(true))
            .catch(() => false)
            .then(run);
        narrativeInFlightRef.current = pass;
        try {
            return await pass;
        } finally {
            if (narrativeInFlightRef.current === pass) {
                narrativeInFlightRef.current = null;
            }
        }
    }, [visitId, sectionId]);

    const handleNarrativeChange = useCallback(
        (text: string) => {
            setNarrativeText(text);
            narrativeDirtyRef.current = true;
            if (narrativeSaveTimerRef.current) {
                clearTimeout(narrativeSaveTimerRef.current);
            }
            narrativeSaveTimerRef.current = setTimeout(() => {
                void saveNarrativeNow().then((saved) => {
                    if (!saved) {
                        setError(
                            "Couldn't save narrative — check your connection. We'll retry when you tap Complete."
                        );
                    }
                });
                // Drop pending AI suggestions when typing settles. Tying
                // this to the same 1500ms debounce avoids wiping all
                // pending on the very first keystroke (which was the
                // pre-fix behaviour) — the RN gets a moment to glance at
                // the suggestions while typing without them evaporating
                // mid-thought.
                //
                // But NEVER while a scan is in flight. dropUnselectedAiSuggestions
                // aborts `scanAbortRef`, and this timer fires 1500ms after typing
                // stops — comfortably inside the 7-23s a scan takes — so a nurse
                // who touches the narrative after tapping Suggest Diagnosis had
                // the request she just asked for cancelled out from under her.
                //
                // Trade-off, made knowingly: since 2026-09-19 the ICD prompt DOES
                // read the narrative (`buildContext`, ai/context-builder.ts), so a
                // scan started before this edit was built from the older text and
                // may miss what she just wrote. That costs her one more tap on
                // Suggest; cancelling the scan she asked for costs her the whole
                // wait. Answer edits still abort via applyChange, as before.
                if (!scanAbortRef.current) {
                    dropUnselectedAiSuggestions();
                }
            }, 1500);
        },
        [saveNarrativeNow, dropUnselectedAiSuggestions, setError]
    );

    // Unmount cleanup — clear the debounce timer AND fire a final flush if
    // the local narrative is still ahead of the server. Without this, the
    // RN typing "patient denies pain" + swiping back within the 1500ms
    // debounce window would silently lose the text (timer cleared without
    // ever calling saveNarrative).
    useEffect(() => {
        return () => {
            if (narrativeSaveTimerRef.current) {
                clearTimeout(narrativeSaveTimerRef.current);
                narrativeSaveTimerRef.current = null;
            }
            if (narrativeDirtyRef.current && visitId && sectionId) {
                // Fire-and-forget — the component is unmounting; no UI to
                // surface a snackbar on, but at least the server gets the
                // text. The next focus-refresh will pull it back if the
                // RN re-enters.
                void saveNarrative(visitId, sectionId, narrativeLatestRef.current).catch(() => {});
            }
        };
        // visit/section don't change during a single section visit — empty
        // deps is correct for the "fire once on unmount" semantics. The
        // latest text is read via narrativeLatestRef.
    }, []);

    // ── Required-field validation ──
    // Mirrors the main-branch wizard's validateRequired: iterates the
    // branching-filtered visible set so hidden targets don't get flagged.
    const validateRequired = useCallback((): boolean => {
        const missing = new Set<string>();
        for (const q of visibleQuestions) {
            const triggeredByBranching = (q.branchingRules ?? []).length > 0;
            if (!q.mandatory && !triggeredByBranching) {
                continue;
            }
            if (PLACEHOLDER_TYPES.has(q.type)) {
                continue;
            }
            const val = answers[q.id];
            if (!val || val.trim() === "" || val.trim() === "[]") {
                missing.add(String(q.id));
            }
            // A bodyMetrics answer is JSON, so the emptiness check above clears
            // as soon as the nurse types one digit — the section would count as
            // answered with a weight, no height and no BMI. Require the card to
            // actually be filled in.
            if (q.type === "bodyMetrics" && !isBodyMetricsComplete(val)) {
                missing.add(String(q.id));
            }
            if (q.type === "customSignature" && !q.config?.hideNameFieldSignature) {
                const children = childQuestionsByParent.get(q.id);
                if (children) {
                    for (const child of children) {
                        if (!child.mandatory) {
                            continue;
                        }
                        const childVal = answers[child.id];
                        if (!childVal || childVal.trim() === "") {
                            missing.add(String(q.id));
                        }
                    }
                }
            }
        }
        if (missing.size > 0) {
            setRequiredErrors(missing);
            setError(
                `${missing.size} required question${missing.size > 1 ? "s" : ""} not answered`
            );
            const firstId = visibleQuestions.find((q) => missing.has(String(q.id)))?.id;
            if (firstId) {
                const y = questionYPositions.current.get(String(firstId));
                if (y != null) {
                    scrollRef.current?.scrollTo({ y: Math.max(0, y - 20), animated: true });
                }
            }
            return false;
        }
        return true;
    }, [visibleQuestions, answers, childQuestionsByParent, setError]);

    // Mirror validateRequired into a ref so async callers (handleValidatePress)
    // can read the latest closure AFTER flushing draft-holding question
    // components. Without the ref, the async callback would still hold a
    // closure over the stale `answers` it captured before the flush.
    //
    // Writing to the ref during render is React-legal (refs are not state)
    // and safe under StrictMode/Concurrent rendering: this ref is only read
    // by event handlers, which fire after commits. A double-rendered or
    // aborted-render mutation is harmless — the ref just gets re-written to
    // the same value (StrictMode) or stays stale until the next render commits
    // (Concurrent abort), and event handlers don't run during render anyway.
    const validateRequiredRef = useRef(validateRequired);
    validateRequiredRef.current = validateRequired;

    // Draft registry — child question components that hold uncommitted local
    // drafts (NumberQuestion in particular) register a `flushIfDirty` callback
    // here on mount. handleValidatePress iterates the registry and synchronously
    // flushes every dirty draft before running validation. This bypasses the
    // OS keyboard / JS<->native bridge entirely on the commit path: the flush
    // is a plain JS call that synchronously routes through setAnswer. A short
    // yield after the flushAll() lets React commit the queued setStates before
    // validateRequiredRef.current() reads the new closure.
    const draftHoldersRef = useRef(new Map<string, DraftHolder>());
    const draftRegistry = useMemo<DraftRegistry>(
        () => ({
            register: (id, holder) => {
                draftHoldersRef.current.set(id, holder);
            },
            unregister: (id) => {
                draftHoldersRef.current.delete(id);
            },
            flushAll: () => {
                for (const holder of draftHoldersRef.current.values()) {
                    holder.flushIfDirty();
                }
            }
        }),
        []
    );

    /**
     * Commit everything the RN has entered but not yet sent, and resolve only
     * once the server has it.
     *
     * The section AI reads answers from the database; the autosave holds them
     * on the device for up to 3s. Anything that asks the server to reason about
     * this section must go through here first, or it can be handed a section the
     * server has not been told about yet — see the KNOWN RACE note on runScan
     * and GOTCHAS #66.
     *
     * Three stages, because there are three places the RN's words can be hiding:
     *   1. `draftRegistry.flushAll()` — uncommitted child drafts (NumberQuestion
     *      holds one and commits on blur). Synchronous, routes through setAnswer.
     *      The RAF yield lets React commit those setStates first.
     *   2. `flushSaves()` — the autosave engine. Returns true only once the
     *      server has committed; the engine keeps one request in the air, so
     *      this waits for at most two.
     *   3. `saveNarrativeNow()` — the narrative, on its own 1500ms saver.
     *
     * (2) and (3) run together: different endpoints, no ordering between them,
     * so this costs one round trip rather than two.
     *
     * Every caller flushes all three, including the diagnosis scan. Since
     * 2026-09-19 the ICD prompt reads the narrative: `buildContext` in
     * `ai/context-builder.ts` renders `visit_section_narrative` inside the
     * section's SECTION_DATA (prod visit 420443: the Pain narrative said
     * "diagnosis of multiple sclerosis" and the scan, which could not see it,
     * never suggested G35). So the narrative flush here is load-bearing for the
     * scan, not just for durability. It is close to free: `saveNarrativeNow`
     * returns early without a request when the narrative is not dirty.
     */
    const flushPendingWrites = useCallback(async (): Promise<FlushOutcome> => {
        draftRegistry.flushAll();
        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
        const [answersOk, narrativeOk] = await Promise.all([flushSaves(), saveNarrativeNow()]);
        if (!answersOk) {
            return { ok: false, what: "answers" };
        }
        if (!narrativeOk) {
            return { ok: false, what: "narrative" };
        }
        return { ok: true };
    }, [draftRegistry, flushSaves, saveNarrativeNow]);

    // ── Diagnoses lifecycle ──
    //
    // Auto-transition idle → results ONLY when there are PENDING AI rows
    // (suggestedByAi && !confirmed && !alreadyOnProfile). Selected rows live
    // in Zone 2 BELOW the band — they shouldn't bump the band out of idle.
    // Profile / cross-section codes live in Zone 3 — also shouldn't bump it.
    //
    // This keeps the canonical chapter-03 behavior: after auto-delete of
    // pending suggestions (on answer edit, narrative edit, or Complete tap),
    // the band returns to the Ready / Suggest CTA — but Zone 2's selected
    // codes stay visible below.
    const pendingRowCount = useMemo(
        () => rows.filter((d) => d.suggestedByAi && !d.confirmed && !d.alreadyOnProfile).length,
        [rows]
    );
    useEffect(() => {
        if (pendingRowCount > 0 && diagnosesPhase === "idle") {
            setDiagnosesPhase("results");
        }
    }, [pendingRowCount, diagnosesPhase]);

    // (No hydrate effect needed — `rows` derives from `serverDiagnoses` on the
    // first render, so a section with pre-existing confirmed codes shows its
    // Zone 2 immediately without a setState round-trip.)

    // The AI reads this section from the database, so it MUST NOT be called
    // while answers are still sitting on the phone. Prod visit 420517
    // (26 Aug 2026): four neurological findings, including seizures, committed
    // ~0.5s after the ICD call had already read the section, and the model
    // correctly reported "nothing documented" for a section the nurse had just
    // filled in. Gated on flushPendingWrites below. Full write-up: GOTCHAS #66.
    const runScan = useCallback(async () => {
        if (!visitId || !sectionId) {
            return;
        }
        // Gate the read on the write. Before anything else, and before we mark
        // the section as scanned — a scan that never ran must not satisfy the
        // has-scanned-once gate in handleValidatePress.
        const flushed = await flushPendingWrites();
        if (!flushed.ok) {
            setError(flushErrorMessage(flushed.what));
            return;
        }
        // Abort any prior in-flight scan + arm a new AbortController.
        // dropUnselectedAiSuggestions also signals abort, so an ANSWER edit
        // during a scan kills the request — the stale result never lands.
        // A narrative edit during a scan does not abort it, by choice — see
        // handleNarrativeChange for the trade-off.
        if (scanAbortRef.current) {
            scanAbortRef.current.abort();
        }
        const controller = new AbortController();
        scanAbortRef.current = controller;
        hasScannedRef.current = true;
        setDiagnosesPhase("analyzing");
        try {
            // Rebuild against fresh server state too — refreshServerDiagnoses
            // also serializes behind any in-flight write.
            await refreshServerDiagnoses();
            const result = await fetchIcdSuggestions(visitId, sectionId, {
                signal: controller.signal
            });
            // Aborted between request start + return → stale, don't apply.
            if (controller.signal.aborted) {
                return;
            }
            // Store the suggestions; `rows` re-derives them against the fresh
            // serverDiagnoses. No optimistic list to merge.
            setSuggestions(result.icdSuggestions);
            setDiagnosesPhase("results");
            scanAbortRef.current = null;
        } catch (e) {
            // Aborts are expected — silently drop back without surfacing
            // an error band.
            if (controller.signal.aborted) {
                return;
            }
            setDiagnosesPhase("errored");
            setError(e instanceof Error ? e.message : "AI scan failed");
            scanAbortRef.current = null;
        }
    }, [visitId, sectionId, refreshServerDiagnoses, flushPendingWrites, setError]);

    // ── Diagnosis writes — pessimistic, server-confirmed, via runWrite ──
    // The displayed `rows` reflect only what the server has confirmed. Each
    // write awaits the server, then re-reads; on failure the row stays in its
    // prior server state with a surfaced error. No optimistic mutation, no
    // fire-and-forget, no id-patch.
    const toggleConfirm = useCallback(
        (code: string) => {
            if (readOnly || !visitId || !sectionId) {
                return;
            }
            const row = rows.find((r) => r.code === code);
            if (!row || row.alreadyOnProfile) {
                return;
            }
            if (!row.confirmed) {
                // Select — persist; the refresh then shows it in Zone 2.
                void runWrite(
                    code,
                    () =>
                        saveDiagnoses(visitId, sectionId, [
                            {
                                code: row.code,
                                description: row.description ?? "",
                                suggestedByAi: row.suggestedByAi
                            }
                        ]).then(() => undefined),
                    `Couldn't save ${code} — check your connection and try again.`
                );
            } else if (row.id != null) {
                // Unselect — DELETE; the refresh then removes it.
                const id = row.id;
                void runWrite(
                    code,
                    () => deleteVisitDiagnosisById(visitId, id).then(() => undefined),
                    `Couldn't remove ${code} — check your connection and try again.`
                );
            }
        },
        [rows, runWrite, readOnly, visitId, sectionId]
    );

    const addManually = useCallback(
        (code: string, description: string) => {
            if (readOnly || !visitId || !sectionId) {
                return;
            }
            if (rows.some((r) => r.code === code)) {
                return;
            }
            void runWrite(
                code,
                () =>
                    saveDiagnoses(visitId, sectionId, [
                        { code, description, suggestedByAi: false }
                    ]).then(() => undefined),
                `Couldn't save ${code} — check your connection and try again.`
            );
        },
        [rows, runWrite, readOnly, visitId, sectionId]
    );

    // Persist an answer-based (matrix) suggestion through the normal save path,
    // tagged source='matrix'. The refetch then moves it into the Selected zone
    // and the answer-based group filter drops it.
    const selectAnswerBased = useCallback(
        (suggestion: AnswerBasedSuggestion) => {
            if (readOnly || !visitId || !sectionId) {
                return;
            }
            if (rows.some((r) => r.code === suggestion.icdCode)) {
                return;
            }
            void runWrite(
                suggestion.icdCode,
                () =>
                    saveDiagnoses(visitId, sectionId, [
                        {
                            code: suggestion.icdCode,
                            description: suggestion.icdText,
                            suggestedByAi: false,
                            source: "matrix"
                        }
                    ]).then(() => undefined),
                `Couldn't save ${suggestion.icdCode} — check your connection and try again.`
            );
        },
        [rows, runWrite, readOnly, visitId, sectionId]
    );

    const removeRn = useCallback(
        (code: string) => {
            if (readOnly || !visitId) {
                return;
            }
            const row = rows.find(
                (r) => r.code === code && !r.suggestedByAi && !r.alreadyOnProfile
            );
            if (!row || row.id == null) {
                return;
            }
            const id = row.id;
            void runWrite(
                code,
                () => deleteVisitDiagnosisById(visitId, id).then(() => undefined),
                `Couldn't remove ${code} — check your connection and try again.`
            );
        },
        [rows, runWrite, readOnly, visitId]
    );

    // (persistDiagnoses removed — diagnoses are persisted per-Select via
    // runWrite, so Complete no longer re-POSTs the list. That re-POST was the
    // resurrection's write step; removing it closes the resurrection at the DB
    // level regardless of any transient stale read.)

    // ── Complete-section flow ──
    const finishSection = useCallback(async () => {
        if (!visitId || !sectionId) {
            return;
        }
        setCompleting(true);
        setError(null);
        try {
            // Answers, child drafts and the narrative — all of it committed
            // before the section is marked done.
            const flushed = await flushPendingWrites();
            if (!flushed.ok) {
                setError(flushErrorMessage(flushed.what));
                setCompleting(false);
                return;
            }
            await completeSection(form.visit.id, sectionId);
            updateForm((prev) => ({
                ...prev,
                sectionStatuses: [
                    ...prev.sectionStatuses.filter((s) => String(s.sectionId) !== sectionId),
                    {
                        id: sectionId,
                        visitId: form.visit.id,
                        sectionId,
                        status: "completed",
                        completedAt: new Date().toISOString()
                    }
                ]
            }));
            setValidated(true);
            setTimeout(() => {
                // Use back() to pop the stack — see docs/back-navigation-audit.md
                // and PR #255. router.replace() as a "go back" creates duplicate
                // stack entries. The only acceptable replace() here is the
                // deep-link / refresh fallback when canGoBack() is false; in that
                // case we land on the assessment sections screen (one level deeper
                // than the visit main) so the RN can immediately pick the next
                // section instead of being stranded.
                if (router.canGoBack()) {
                    router.back();
                } else {
                    router.replace(`/visit/${visitId}/assessment`);
                }
            }, 1200);
        } catch (e) {
            setError(e instanceof Error ? e.message : "Failed to complete section");
        } finally {
            setCompleting(false);
        }
    }, [visitId, sectionId, flushPendingWrites, form.visit.id, updateForm, router, setError]);

    // ── Scroll-to + derived counts (must precede the phase handlers) ─────
    const scrollToY = useCallback((y: number, offset = 24) => {
        scrollRef.current?.scrollTo({ y: Math.max(0, y - offset), animated: true });
    }, []);

    /**
     * "Effective" active errors — those the RN hasn't dismissed yet. The
     * old "narrative-saved" status is gone (no more inline composer), so
     * the only path off the active list is Acknowledge & dismiss.
     */
    const unresolvedErrors = useMemo(
        () => validation.activeErrors.filter((e) => e.id != null && !dismissedLocalIds.has(e.id)),
        [validation.activeErrors, dismissedLocalIds]
    );
    const requiredCount = unresolvedErrors.filter((e) => e.severity === "hard").length;
    const suggestedCount = unresolvedErrors.length - requiredCount;

    /**
     * Fire /ai/validate and transition phase according to outcome. Used by
     * both the initial Complete tap and the Re-validate CTA.
     *
     * IMPORTANT: read the outcome from `triggerValidation()`'s return value,
     * NOT from `validation.validationFailed` / `validation.remainingCount`
     * off the closure. Those are stale-closure snapshots from before the
     * hook's state writes settle — reading them silently auto-sealed
     * sections that had just received hard errors. See the Visit-127
     * discipline comment in CompleteChapter.tsx.
     */
    // Review can remain open while answers, branches or diagnoses are edited.
    const completionStateRef = useRef({ rows, readOnly, validation });
    completionStateRef.current = { rows, readOnly, validation };
    const runValidation = useCallback(
        async (initial: boolean) => {
            // Retry/revalidation also flush drafts and review new differences.
            draftRegistry.flushAll();
            await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
            if (completionStateRef.current.readOnly || !validateRequiredRef.current()) {
                return;
            }
            if (!(await answerReview.review())) {
                return;
            }
            draftRegistry.flushAll();
            await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
            if (completionStateRef.current.readOnly || !validateRequiredRef.current()) {
                return;
            }
            // Same gate as runScan — /ai/validate reads this section's answers
            // from the database. Bail before entering the validating phase so
            // the pill stays where it was and the RN can simply tap again.
            // build-validation-prompt.ts renders each section's narrative into
            // the prompt, so validating before it is saved would judge the RN on
            // text the server has not been told about.
            const flushed = await flushPendingWrites();
            if (!flushed.ok) {
                setError(flushErrorMessage(flushed.what));
                return;
            }
            setCompletePhase(initial ? "validating" : "revalidating");
            validation.hasTriggeredRef.current = true;
            // Reveal the Complete band's expanded result/counters without yanking
            // the viewport. The band is the last chapter and the RN taps Complete
            // from inside it, so it's already in view — we only nudge DOWN to the
            // end (after layout settles) to surface the grown band on each outcome.
            // We deliberately do NOT snap the band to the top: that lurched the
            // viewport up ~a screen when Complete was tapped from the bottom (the
            // usual case). And we do not scroll on entry — nothing has grown yet.
            const revealBand = () => {
                requestAnimationFrame(() => scrollRef.current?.scrollToEnd({ animated: true }));
            };
            let outcome: { failed: boolean; activeErrors: ValidationError[] } | { cancelled: true };
            try {
                outcome = await completionStateRef.current.validation.triggerValidation();
            } catch (e) {
                setCompletePhase("failed");
                setError(e instanceof Error ? e.message : "Clinical review failed");
                revealBand();
                return;
            }
            // Leaving the section (or backgrounding the app and coming back)
            // aborts its validation request. An abort is not a clean result,
            // so never schedule completion for it: the server keeps running the
            // aborted request and writes its errors after the section would
            // already be complete.
            if ("cancelled" in outcome) {
                return;
            }
            if (outcome.failed) {
                setCompletePhase("failed");
                revealBand();
                return;
            }
            if (outcome.activeErrors.length === 0) {
                setCompletePhase("clear");
                revealBand();
                completionTimerRef.current = setTimeout(() => {
                    completionTimerRef.current = null;
                    void finishSection();
                }, 1200);
            } else {
                setCompletePhase("errors");
                revealBand();
            }
        },
        [
            validation,
            finishSection,
            flushPendingWrites,
            setError,
            answerReview.review,
            draftRegistry
        ]
    );

    /**
     * Entry point — RN tapped Complete from the idle pill. Order of gates:
     *
     *   1. Required-field check  — short-circuits with red marks + scroll.
     *   2. Local patient-context review — dismissals persist on this device.
     *   3. Has-scanned-once gate — for sections that have a Diagnoses chapter,
     *      the RN must have either run the AI scan once in this screen entry
     *      OR already saved at least one diagnosis to this section. Saved
     *      same-section rows can come from AI, answer-based matrix suggestions,
     *      or manual add. Profile / cross-section rows and pending Z1
     *      suggestions do not count.
     *   4. If the section has no Complete chapter (e.g. medical_history),
     *      go straight to finishSection.
     *   5. Otherwise run validation.
     */
    const handleValidatePress = useCallback(async () => {
        // Synchronously flush every uncommitted draft (NumberQuestion is the
        // only consumer today — it holds a local draft and commits on blur).
        // flushAll() calls each registered flushIfDirty() in turn; each one
        // synchronously routes through setAnswer if its draft differs from the
        // committed value. No bridge round-trip — these are plain JS calls.
        //
        // We then yield via requestAnimationFrame so React can commit the queued
        // setStates and re-render before validateRequiredRef.current() reads
        // the new closure. RAF is stronger than setTimeout(0) here: it fires
        // after the next paint, which is guaranteed to be after any pending
        // commit, including under React Concurrent rendering where renders may
        // be split across tasks.
        if (readOnly || completing || completionBusy.current) {
            return;
        }
        completionBusy.current = true;
        try {
            draftRegistry.flushAll();
            await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
            if (!validateRequiredRef.current()) {
                return;
            }
            if (!(await answerReview.review())) {
                return;
            }
            // The nurse can edit answers while the review box is open.
            draftRegistry.flushAll();
            await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
            if (completionStateRef.current.readOnly || !validateRequiredRef.current()) {
                return;
            }
            if (chapters.diagnoses) {
                const hasSectionDiagnosis = hasDiagnosisAddedToThisSection(
                    completionStateRef.current.rows
                );
                if (!hasScannedRef.current && !hasSectionDiagnosis) {
                    setError(
                        "Tap Run AI scan in Diagnoses at least once before completing this section."
                    );
                    const y = diagnosesYRef.current;
                    if (y != null) {
                        scrollToY(y, 24);
                    }
                    return;
                }
            }
            if (!chapters.complete) {
                await finishSection();
                return;
            }
            await runValidation(true);
        } finally {
            completionBusy.current = false;
        }
        // `validateRequired` is intentionally omitted from the dep array —
        // we read it via validateRequiredRef.current() so handleValidatePress
        // stays stable across answer changes. Without this, the callback (and
        // every Pressable that holds it as a prop) would re-create on every
        // keystroke. The repo's eslint config has no react-hooks/exhaustive-deps
        // rule enabled, so no suppression directive is needed here.
    }, [
        readOnly,
        completing,
        answerReview.review,
        chapters.complete,
        chapters.diagnoses,
        draftRegistry,
        finishSection,
        rows,
        runValidation,
        scrollToY,
        setError
    ]);

    /**
     * "Take me to the first item" — scroll to first unresolved error's
     * question and switch the band to the "resolve" tone. Falls back to
     * scroll-to-top if every remaining error is orphan (no questionId) —
     * old wizard behavior.
     */
    const handleTakeMeToFirst = useCallback(() => {
        const firstQid = unresolvedErrors.find((e) => e.questionId)?.questionId ?? null;
        if (firstQid) {
            const y = questionYPositions.current.get(firstQid);
            if (y != null) {
                scrollToY(y, 80);
            }
        } else {
            // Orphan-only error set — scroll to top so the "General" header
            // is visible.
            scrollToY(0, 0);
        }
        setCompletePhase("resolve");
    }, [unresolvedErrors, scrollToY]);

    /**
     * "Re-validate" — clear local UI state and re-run validation. The
     * DELETE for each acknowledged dismissal has already fired at the
     * moment of tap (see handleAcknowledge), so no batched DELETEs here.
     */
    const handleRevalidate = useCallback(async () => {
        setDismissedLocalIds(new Set());
        await runValidation(false);
    }, [runValidation]);

    const handleRetry = useCallback(async () => {
        await runValidation(true);
    }, [runValidation]);

    const handleCloseFailed = useCallback(() => {
        setCompletePhase("idle");
    }, []);

    /**
     * "Add narrative" on a validation error card → scrolls down to Chapter 02
     * (the Narrative chapter). The RN edits the narrative in the actual
     * narrative box — same single source of truth. No inline composer.
     */
    const handleAddNarrative = useCallback(
        async (_err: ValidationError) => {
            if (narrativeTargetRef.current) {
                await scrollToView(narrativeTargetRef.current);
                setNarrativeHighlightTick((tick) => tick + 1);
            }
        },
        [scrollToView]
    );

    const handleAcknowledge = useCallback(
        (errorId: number) => {
            // Fire the DELETE immediately. The hook handles optimistic-update
            // with rollback + snackbar on failure (see useValidationFlow's
            // dismissError). Without this, dismissals only landed in the
            // local set and DELETE was deferred to Re-validate — so if the
            // RN backed out of the section before re-validating, the focus-
            // effect refetched the un-deleted error from the server and the
            // dismissed card reappeared.
            validation.dismissError(errorId);
            setDismissedLocalIds((prev) => {
                const out = new Set(prev);
                out.add(errorId);
                return out;
            });
        },
        [validation]
    );

    const handleAddDiagnosis = useCallback(
        (_err: ValidationError) => {
            const y = addManuallyYRef.current ?? diagnosesYRef.current;
            if (y != null) {
                scrollToY(y, 24);
            }
            setAddManuallyHighlightTick(Date.now());
        },
        [scrollToY]
    );

    // Auto-transition: when in errors / resolve and every error is addressed,
    // flip the band to "resolved" so the RN sees the Re-validate CTA.
    //
    // The prior `validation.activeErrors.length > 0` guard was a footgun: the
    // hook's optimistic dismiss drops the dismissed error from activeErrors in
    // the SAME render as unresolvedErrors going to 0, so the guard fails
    // exactly when the transition is needed and the band gets stuck on amber
    // with "0 items to resolve". The "errors/resolve" phase check is the only
    // gate we need — those phases only exist after triggerValidation actually
    // found errors, so we can trust them as a "there were errors" signal.
    useEffect(() => {
        if (
            (completePhase === "errors" || completePhase === "resolve") &&
            unresolvedErrors.length === 0
        ) {
            setCompletePhase("resolved");
        }
    }, [completePhase, unresolvedErrors.length]);

    // Auto-restore on re-entry: when the server already has unresolved
    // validation errors and the band is sitting at idle (RN navigated away
    // mid-resolve and came back), promote the band to "errors" so the green
    // "Complete section" pill doesn't show. Without this guard, tapping
    // Complete re-runs the LLM — wasted credit + non-deterministic verdict
    // divergence against the verdict the RN already saw.
    useEffect(() => {
        if (completePhase === "idle" && unresolvedErrors.length > 0) {
            setCompletePhase("errors");
        }
    }, [completePhase, unresolvedErrors.length]);

    // ── Early-return dispatch for non-flat behavior types ──
    //
    // onComplete here MUST call finishSection (not router.back) so the section
    // gets marked complete server-side. The flat-section redesign (90805da3,
    // 2026-05-15) deleted the wizard-era `handleFinalStep` and rewired these
    // two callsites to `router.back()`. That looked harmless because the new
    // chapters flow has its own CompleteChapter → finishSection path for
    // clinical sections, but POC and Supervisory are early-return components
    // that bypass the chapters entirely — their onComplete prop is the only
    // place that fires POST /sections/:id/complete. With router.back(), the
    // user signs, lands back on the dashboard, sees the section still
    // incomplete, taps in again, loops. Verified across visits 217 / 273 / 274
    // (May 19, all stuck after their bundle picked up the OTA from this
    // commit). finishSection is in scope from line ~732 above; for
    // plan_of_care + supervisory_evaluation its chapters config is all false,
    // so it skips narrative + diagnoses saves and just fires completeSection.
    if (!section) {
        return (
            <SafeAreaView style={assessmentScreenStyles.container} edges={["top"]}>
                <View style={styles.center}>
                    <Text style={styles.empty}>Section not found</Text>
                </View>
            </SafeAreaView>
        );
    }

    if (behaviorType === "plan_of_care") {
        return (
            <SafeAreaView style={assessmentScreenStyles.container} edges={["top"]}>
                <PocReviewStep
                    visitId={form.visit.id}
                    sectionId={sectionId ?? ""}
                    sectionDisplayName={section.displayName ?? section.name}
                    onComplete={() => finishSection()}
                    onBack={() =>
                        router.canGoBack() ? router.back() : router.replace(`/visit/${visitId}`)
                    }
                    readOnly={readOnly}
                />
            </SafeAreaView>
        );
    }
    if (behaviorType === "supervisory_evaluation") {
        return (
            <SafeAreaView style={assessmentScreenStyles.container} edges={["top"]}>
                <SupervisoryStep
                    visitId={form.visit.id}
                    sectionId={sectionId ?? ""}
                    scheduledAideName={form.visit.caregiverName}
                    scheduledAidePhone={form.visit.caregiverPhoneNumber}
                    onComplete={() => finishSection()}
                    onBack={() =>
                        router.canGoBack() ? router.back() : router.replace(`/visit/${visitId}`)
                    }
                    readOnly={readOnly}
                    // Same test as the API (visit_types.name). A special visit's
                    // name here is its own label, not the type's, so it never counts.
                    isSupervisoryVisit={
                        form.formSource !== "special" &&
                        isSupervisoryVisitTypeName(form.visitType?.name)
                    }
                    // Back to the visit screen (popping the assessment on the way;
                    // replaced in if it is not in the stack) with the Report an
                    // issue sheet open on "Aide not here".
                    onReportAideAbsent={() =>
                        router.dismissTo(`/visit/${visitId}?reportIssue=aide_not_here`)
                    }
                />
            </SafeAreaView>
        );
    }
    if (behaviorType === "performance_evaluation") {
        return (
            <SafeAreaView style={assessmentScreenStyles.container} edges={["top"]}>
                <PerformanceEvaluationStep
                    visitId={form.visit.id}
                    scheduledAideName={form.visit.caregiverName}
                    scheduledAidePhone={form.visit.caregiverPhoneNumber}
                    onComplete={() => finishSection()}
                    onBack={() =>
                        router.canGoBack() ? router.back() : router.replace(`/visit/${visitId}`)
                    }
                    readOnly={readOnly}
                />
            </SafeAreaView>
        );
    }

    return (
        <DraftRegistryContext.Provider value={draftRegistry}>
            <SafeAreaView style={assessmentScreenStyles.container} edges={["top"]}>
                <AssessmentScreenHeader
                    title={section.displayName ?? section.name}
                    onBack={() =>
                        router.canGoBack() ? router.back() : router.replace(`/visit/${visitId}`)
                    }
                    status={
                        saveStatus === "saving" ? (
                            <Text style={styles.headerStatusText}>Saving…</Text>
                        ) : saveStatus === "saved" ? (
                            <Text style={[styles.headerStatusText, { color: colors.success500 }]}>
                                Saved
                            </Text>
                        ) : saveStatus === "error" ? (
                            <Text style={[styles.headerStatusText, { color: colors.error500 }]}>
                                Save failed
                            </Text>
                        ) : null
                    }
                />

                <ScrollView
                    nativeID="visit-section-scroll"
                    onScroll={onHintScroll}
                    scrollEventThrottle={16}
                    ref={scrollRef}
                    style={assessmentScreenStyles.scroll}
                    contentContainerStyle={assessmentScreenStyles.content}
                    keyboardShouldPersistTaps="handled"
                    keyboardDismissMode="on-drag"
                >
                    {chapters.questions ? (
                        <QuestionsChapter
                            questions={questions}
                            answers={answers}
                            onChange={handleChange}
                            readOnly={readOnly}
                            visitId={visitId ?? ""}
                            sectionId={sectionId ?? ""}
                            patient={form?.patient ?? undefined}
                            errorsByQuestion={validation.errorsByQuestion}
                            orphanErrors={validation.orphanErrors.filter(
                                (e) => e.ruleId !== "NARR-3" && e.ruleId !== "NARR-4"
                            )}
                            dismissedLocalIds={dismissedLocalIds}
                            onAddNarrative={handleAddNarrative}
                            onAddDiagnosis={handleAddDiagnosis}
                            onAcknowledge={handleAcknowledge}
                            requiredErrors={requiredErrors}
                            questionYPositions={questionYPositions}
                            questionRefs={hintQuestionRefs}
                            answerReviewItems={answerReview.pending ?? undefined}
                            dismissingReview={answerReview.saving}
                            onDismissReviewItem={(key) => {
                                void answerReview.dismiss(key);
                            }}
                            contextHints={contextHints}
                            onOpenContextHint={openContextHint}
                        />
                    ) : null}

                    {chapters.narrative ? (
                        <View ref={narrativeTargetRef} collapsable={false}>
                            <NarrativeChapter
                                ref={narrativeRef}
                                visitId={visitId ?? ""}
                                sectionId={sectionId ?? ""}
                                initialText={narrativeText}
                                guidanceText={section.narrativeGuidanceText ?? null}
                                onChange={handleNarrativeChange}
                                readOnly={readOnly}
                                highlightTick={narrativeHighlightTick}
                            />
                            {/* NARR-3 flags anchor to the narrative itself: this
                                section's own narrative names a finding another
                                section's answers contradict. Rendered here, under
                                the box the nurse just wrote in, instead of the
                                General block. */}
                            {validation.orphanErrors
                                .filter((e) => e.ruleId === "NARR-3" || e.ruleId === "NARR-4")
                                .map((err) => (
                                    <ValidationErrorCardWrapper
                                        key={`narr3-${err.id ?? err.message}`}
                                        error={err}
                                        dismissedLocalIds={dismissedLocalIds}
                                        onAddNarrative={handleAddNarrative}
                                        onAddDiagnosis={handleAddDiagnosis}
                                        onAcknowledge={handleAcknowledge}
                                    />
                                ))}
                        </View>
                    ) : null}

                    {chapters.diagnoses ? (
                        <View
                            onLayout={(e) => {
                                diagnosesYRef.current = e.nativeEvent.layout.y;
                                // The "Add manually" button sits inside the results
                                // phase. Capture an approximate y for the scroll
                                // target — for v1 we treat it as the same as the
                                // chapter top; tighter wiring is a follow-up.
                                addManuallyYRef.current = e.nativeEvent.layout.y + 120;
                            }}
                        >
                            <DiagnosesChapter
                                phase={diagnosesPhase}
                                diagnoses={rows}
                                pendingCodes={pendingCodes}
                                sectionName={section.name}
                                onToggleConfirm={toggleConfirm}
                                onAddManually={addManually}
                                onRemoveRn={removeRn}
                                onRunScan={() => {
                                    void runScan();
                                }}
                                readOnly={readOnly}
                                addManuallyHighlightTick={addManuallyHighlightTick}
                                answerBased={answerBased}
                                onSelectAnswerBased={selectAnswerBased}
                            />
                        </View>
                    ) : null}

                    {answerReview.pending !== null ? (
                        <View ref={reviewTargetRef} collapsable={false}>
                            <ContextAnswerReview
                                items={answerReview.pending}
                                saving={answerReview.saving}
                                onDismiss={() => {
                                    void answerReview.dismiss();
                                }}
                                onQuestion={(id) => {
                                    const target = hintQuestionRefs.current.get(id);
                                    if (target) {
                                        scrollToView(target, 24);
                                    }
                                }}
                                onSource={openContextHint}
                            />
                        </View>
                    ) : chapters.complete ? (
                        <View
                            onLayout={(e) => {
                                completeYRef.current = e.nativeEvent.layout.y;
                            }}
                        >
                            <CompleteChapter
                                phase={validated ? "clear" : completePhase}
                                remainingCount={requiredCount + suggestedCount}
                                requiredCount={requiredCount}
                                suggestedCount={suggestedCount}
                                readOnly={readOnly}
                                validatingStageIdx={validatingStageIdx}
                                onValidate={() => {
                                    void handleValidatePress();
                                }}
                                onTakeMeToFirst={handleTakeMeToFirst}
                                onRevalidate={() => {
                                    void handleRevalidate();
                                }}
                                onRetry={() => {
                                    void handleRetry();
                                }}
                                onCloseFailed={handleCloseFailed}
                            />
                        </View>
                    ) : (
                        <View style={styles.simpleCompleteWrap}>
                            <Pressable
                                onPress={() => {
                                    void handleValidatePress();
                                }}
                                disabled={readOnly || completing}
                                accessibilityRole="button"
                                style={({ pressed }) => [
                                    styles.simpleCompleteBtn,
                                    pressed && { opacity: 0.85 },
                                    (readOnly || completing) && { opacity: 0.5 }
                                ]}
                            >
                                <Text style={styles.simpleCompleteText}>Mark section complete</Text>
                                <MaterialCommunityIcons name="arrow-right" size={18} color="#fff" />
                            </Pressable>
                        </View>
                    )}

                    <View style={{ height: 40 }} />
                </ScrollView>
                <ConfirmDialog {...dialogState} onDismiss={dismissDialog} />

                {/* Diagnosis Hub FAB — hidden while the hub sheet is open */}
                {!hubOpen && !readOnly ? (
                    <DiagnosisHubFAB
                        count={hub.count}
                        onPress={() => validation.setActiveSheet("hub")}
                    />
                ) : null}

                <DiagnosisHubSheet
                    visible={hubOpen}
                    diagnoses={hub.diagnoses}
                    onClose={() => validation.setActiveSheet(null)}
                    onDeleteDiagnosis={hub.removeDiagnosis}
                    onChangeClassification={hub.changeClassification}
                    onNavigateToSection={() => {
                        /* hub→section nav is handled at the assessment level */
                    }}
                    onNavigateToMedications={() => {
                        validation.setActiveSheet(null);
                        router.push(`/visit/${visitId}/medications`);
                    }}
                />

                <Snackbar
                    visible={!!localError.msg}
                    onDismiss={() => setError(null)}
                    duration={4000}
                    style={styles.snackbar}
                >
                    {localError.msg ?? ""}
                </Snackbar>

                <ContextItemSheet
                    visitId={visitId ?? ""}
                    items={contextSheet?.items ?? null}
                    title={contextSheet?.title ?? null}
                    questionId={contextSheet?.questionId}
                    onClose={() => setContextSheet(null)}
                />
            </SafeAreaView>
        </DraftRegistryContext.Provider>
    );
}

const styles = StyleSheet.create({
    center: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center"
    },
    empty: {
        fontFamily: fonts.medium,
        color: colors.textSecondary
    },
    headerStatusText: {
        fontFamily: fonts.bold,
        fontSize: 11,
        color: colors.textSecondary,
        letterSpacing: 0.4,
        textTransform: "uppercase"
    },
    simpleCompleteWrap: {
        paddingHorizontal: 22,
        marginTop: 24
    },
    simpleCompleteBtn: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        paddingVertical: 18,
        paddingHorizontal: 22,
        borderRadius: 999,
        backgroundColor: colors.ink,
        ...Platform.select({
            ios: {
                shadowColor: "#0B0B1F",
                shadowOffset: { width: 0, height: 8 },
                shadowOpacity: 0.18,
                shadowRadius: 18
            },
            android: { elevation: 4 },
            default: {}
        })
    },
    simpleCompleteText: {
        fontFamily: fonts.extraBold,
        fontSize: 15,
        color: "#fff",
        letterSpacing: -0.2
    },
    snackbar: {
        backgroundColor: colors.warning500
    }
});
